import { BaseMutationOptions } from '@apollo/client';
import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { ReactNode, useCallback, useRef, useState } from 'react';

import { CompoundHeader } from '@/components/display/CompoundHeader/CompoundHeader';
import { Button } from '@/components/form/baseInputs/Button';
import { Checkbox } from '@/components/form/baseInputs/Checkbox';
import { ButtonGroupInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { AlignCenterIcon } from '@/components/icons/AlignCenterIcon';
import { AlignLeftIcon } from '@/components/icons/AlignLeftIcon';
import { AlignRightIcon } from '@/components/icons/AlignRightIcon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  FileUploaderV2,
  GenerateSignedUploadURLParams,
} from '@/modules/files/FileUploaderV2';
import { UploadedFileData } from '@/modules/files/FileUploaderV2/hooks/useUploadFile';
import { useCustomerTheme } from '@/styles/themes/CustomerThemeProvider';
import { FileContext } from '@/types/schema';
import { getFullyQualifiedTenantURL, getTenant } from '@/utils/tenantUtils';

import {
  acceptedBrandingFileTypes,
  getPathnameFromUploadURL,
} from './BrandingLogoUploader';
import { FormAwareSlider } from './FormAwareSlider';
import {
  UploadSlideBackgroundImageMutation,
  useUploadSlideBackgroundImageMutation,
} from './graphql/SlideConfig.generated';
import { SlideConfigColorInput } from './SlideConfigColorInput';
import { SlideConfigPlaceholders } from './SlideConfigForm';
import {
  BACKGROUND_IMAGE_SCALE_MAX,
  BACKGROUND_IMAGE_SCALE_MIN,
  slideConfigDefaultValues,
} from './SlideConfigForm.constants';
import {
  CoverSlideConfigBodyTextAlignment,
  LOGO_SCALE_MAX,
  LOGO_SCALE_MIN,
  SlideConfigFormShape,
  SlideConfigVariant,
  TEXT_WIDTH_PX_MAX,
  TEXT_WIDTH_PX_MIN,
  TITLE_SIZE_PT_FALLBACK,
  TITLE_SIZE_PT_MAX,
  TITLE_SIZE_PT_MIN,
  TITLE_SIZE_PT_STEP,
} from './SlideConfigForm.types';

/**
 * Left/center/right toggle options for the body text alignment control.
 * Icon-only buttons with accessible labels; left is the default.
 */
const BODY_TEXT_ALIGNMENT_OPTIONS: ButtonGroupInputOption[] = [
  {
    value: CoverSlideConfigBodyTextAlignment.LeftAligned,
    display: '',
    buttonProps: { startIcon: AlignLeftIcon, 'aria-label': 'Align left' },
  },
  {
    value: CoverSlideConfigBodyTextAlignment.CenterAligned,
    display: '',
    buttonProps: { startIcon: AlignCenterIcon, 'aria-label': 'Align center' },
  },
  {
    value: CoverSlideConfigBodyTextAlignment.RightAligned,
    display: '',
    buttonProps: { startIcon: AlignRightIcon, 'aria-label': 'Align right' },
  },
];

/**
 * Shared form fields for cover and divider slide configuration.
 *
 * The divider form has a single "Use all selections from cover slide"
 * checkbox at the top. When checked, all visual fields are disabled and
 * the cover slide's values are used at render time. When unchecked, the
 * divider uses its own values.
 */
interface SlideConfigFormFieldsProps {
  heading: string;
  subheading: string;
  placeholders: SlideConfigPlaceholders;
  preview: ReactNode;
  onSubmit: () => void;
  variant: SlideConfigVariant;
}

export function SlideConfigFormFields({
  heading,
  subheading,
  preview,
  onSubmit,
  placeholders,
  variant,
}: SlideConfigFormFieldsProps) {
  const {
    control,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<SlideConfigFormShape>();

  const values = watch();
  const isDivider = variant === SlideConfigVariant.DIVIDER;
  const isInheriting = isDivider && values.inheritCover;
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const { showFeedback } = useFeedback();
  const { logos } = useCustomerTheme();
  const [showMissingLogoTooltip, setShowMissingLogoTooltip] = useState(false);
  // An empty form value disables the gradient in previews and saved settings.
  const lastGradientColor = useRef('#334155');

  const [generatePresignedURL] = useUploadSlideBackgroundImageMutation({
    onError: (err) => {
      showFeedback(
        'There was an error uploading the background image. Please try again.'
      );
      reportError('error fetching slide background image presigned URL', err);
    },
  });

  const generateUploadURL = useCallback(
    async (
      params: GenerateSignedUploadURLParams,
      mutationOptions: Partial<BaseMutationOptions>
    ) => {
      const queryProps = await generatePresignedURL({
        ...mutationOptions,
        variables: {
          input: {
            contentsSha256: params.hash,
            contentType: params.contentType,
          },
        },
      });

      const signedUploadURL =
        queryProps.data?.uploadPresentationCoverSlideBackgroundImage.uploadURL;
      if (!signedUploadURL) {
        throw new Error('No errors, but no signed upload URL');
      }

      return { signedUploadURL, ...queryProps };
    },
    [generatePresignedURL]
  );

  const onUploadComplete = useCallback(
    async (
      uploaded: UploadedFileData<UploadSlideBackgroundImageMutation>[]
    ) => {
      if (isEmpty(uploaded)) return;

      const { result } = uploaded[0]!;
      const uploadPath = getPathnameFromUploadURL(
        result?.data?.uploadPresentationCoverSlideBackgroundImage.uploadURL ??
          ''
      );
      const tenantSubdomain = getTenant();
      const tenantUrl = getFullyQualifiedTenantURL(tenantSubdomain);

      setValue('backgroundImageUrl', `${tenantUrl}${uploadPath}`, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <CompoundHeader heading={heading} subheading={subheading} />
        <Button
          size="sm"
          variant="primary"
          type="button"
          loading={isSubmitting}
          onClick={onSubmit}
        >
          Save changes
        </Button>
      </Stack>
      {isDivider && (
        <FormAwareCheckbox<SlideConfigFormShape>
          label="Use all selections from cover slide"
          fieldName="inheritCover"
          control={control}
        />
      )}
      <Divider />
      <Stack direction="row" spacing={4} alignItems="stretch">
        <Box width={360} flexShrink={0} sx={{ position: 'relative' }}>
          <Stack
            component="form"
            spacing={3}
            aria-label="Slide branding settings"
            tabIndex={0}
            // The preview sets the row height; settings scroll within that height.
            sx={{ position: 'absolute', inset: 0, overflowY: 'auto', pr: 3 }}
          >
            {/* Background image */}
            <Stack spacing={2}>
              <Typography variant="title-s">Background image</Typography>
              <FileUploaderV2<UploadSlideBackgroundImageMutation>
                acceptTypes={acceptedBrandingFileTypes}
                onUploadComplete={onUploadComplete}
                generateSignedUploadURL={generateUploadURL}
                householdId={null}
                showExistingDocuments={false}
                context={FileContext.Logo}
                disabled={isInheriting}
              />
              {values.backgroundImageUrl && (
                <Card variant="outlined" sx={{ p: 1 }}>
                  <img
                    style={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                    }}
                    src={values.backgroundImageUrl}
                    alt="Background preview"
                  />
                </Card>
              )}
              <Button
                size="xs"
                variant="secondary"
                fullWidth
                disabled={isInheriting || !values.backgroundImageUrl}
                onClick={() =>
                  setValue('backgroundImageUrl', '', {
                    shouldDirty: true,
                  })
                }
              >
                Clear image
              </Button>
              <FormAwareSlider
                label="Background image scale"
                fieldName="backgroundImagePercentScale"
                min={BACKGROUND_IMAGE_SCALE_MIN}
                max={BACKGROUND_IMAGE_SCALE_MAX}
                fallbackValue={Number(placeholders.backgroundImagePercentScale)}
                input={{
                  kind: 'percent',
                  maxValue: BACKGROUND_IMAGE_SCALE_MAX,
                }}
                // Scaling has no visual effect without an image, so keep
                // the slider locked until one is uploaded.
                disabled={isInheriting || !values.backgroundImageUrl}
              />
              <FormAwareSlider
                label="Background opacity"
                fieldName="backgroundAlpha"
                min={0}
                max={100}
                fallbackValue={Number(placeholders.backgroundAlpha)}
                input={{ kind: 'percent' }}
                disabled={isInheriting}
              />
            </Stack>
            <Divider />

            {/* Background color */}
            <Stack spacing={2}>
              <Typography variant="title-s">Background color</Typography>
              <SlideConfigColorInput<SlideConfigFormShape>
                heading="Background color"
                subheading="Hex color for the slide background"
                fieldName="backgroundColor"
                control={control}
                colorValue={
                  values.backgroundColor ||
                  slideConfigDefaultValues.backgroundColor
                }
                disabled={isInheriting}
              />
              <Checkbox
                label="Use gradient / ombré background"
                value={!!values.backgroundGradientEndColor}
                disabled={isInheriting}
                onChange={(event) => {
                  if (!event.target.checked) {
                    lastGradientColor.current =
                      values.backgroundGradientEndColor;
                  }
                  setValue(
                    'backgroundGradientEndColor',
                    event.target.checked ? lastGradientColor.current : '',
                    { shouldDirty: true }
                  );
                }}
              />
              {!!values.backgroundGradientEndColor && (
                <>
                  <SlideConfigColorInput<SlideConfigFormShape>
                    heading="Gradient end color"
                    subheading="Blend from the background color to this color"
                    fieldName="backgroundGradientEndColor"
                    control={control}
                    colorValue={values.backgroundGradientEndColor}
                    disabled={isInheriting}
                  />
                  <FormAwareSlider
                    label="Gradient direction"
                    fieldName="backgroundGradientAngle"
                    min={0}
                    max={360}
                    fallbackValue={90}
                    input={{ kind: 'number', unit: '°' }}
                    disabled={isInheriting}
                  />
                </>
              )}
            </Stack>
            <Divider />

            <Stack spacing={2}>
              <Typography variant="title-s">Text settings</Typography>
              <SlideConfigColorInput<SlideConfigFormShape>
                heading="Title text color"
                subheading="Hex color for the slide title"
                fieldName="textColor"
                control={control}
                colorValue={
                  values.textColor || slideConfigDefaultValues.textColor
                }
                disabled={isInheriting}
              />
              <SlideConfigColorInput<SlideConfigFormShape>
                heading="Body text color"
                subheading="Hex color for the slide body content"
                fieldName="bodyTextColor"
                control={control}
                colorValue={
                  values.bodyTextColor || slideConfigDefaultValues.bodyTextColor
                }
                disabled={isInheriting}
              />
              <FormAwareButtonGroup<SlideConfigFormShape>
                label="Body text alignment"
                fieldName="bodyTextAlignment"
                control={control}
                options={BODY_TEXT_ALIGNMENT_OPTIONS}
                disabled={isInheriting}
              />

              <FormAwareSlider
                label="Title text size"
                fieldName="titleSizePt"
                min={TITLE_SIZE_PT_MIN}
                max={TITLE_SIZE_PT_MAX}
                step={TITLE_SIZE_PT_STEP}
                fallbackValue={TITLE_SIZE_PT_FALLBACK}
                input={{ kind: 'number', unit: 'pt', decimalScale: 1 }}
                disabled={isInheriting}
              />

              <FormAwareSlider
                label="Text box width"
                fieldName="textWidth"
                min={TEXT_WIDTH_PX_MIN}
                max={TEXT_WIDTH_PX_MAX}
                // An unset width stretches the text block across the slide.
                fallbackValue={TEXT_WIDTH_PX_MAX}
                input={{ kind: 'number', unit: 'px' }}
                disabled={isInheriting}
              />
              <Button
                size="xs"
                variant="secondary"
                fullWidth
                // Reset only the text box position and width. Keep the
                // colors, alignment, and size settings.
                disabled={
                  isInheriting ||
                  (values.textOriginX == null &&
                    values.textOriginY == null &&
                    values.textWidth === '')
                }
                onClick={() => {
                  setValue('textOriginX', null, { shouldDirty: true });
                  setValue('textOriginY', null, { shouldDirty: true });
                  setValue('textWidth', '', { shouldDirty: true });
                  trackUserEvent('presentation text_position_reset', {
                    variant,
                  });
                }}
              >
                Reset text position
              </Button>
            </Stack>
            <Divider />

            <Stack spacing={2}>
              <Typography variant="title-s">Logo settings</Typography>
              <FormAwareSlider
                label="Logo size"
                fieldName="logoPercentScale"
                min={LOGO_SCALE_MIN}
                max={LOGO_SCALE_MAX}
                fallbackValue={Number(placeholders.logoPercentScale)}
                input={{ kind: 'percent', maxValue: LOGO_SCALE_MAX }}
                disabled={isInheriting}
              />

              <Tooltip
                title="No logo for darker backgrounds is uploaded. Upload one in the Brand logos tab."
                open={
                  showMissingLogoTooltip &&
                  !logos.secondaryWide &&
                  !isInheriting
                }
                onClose={() => setShowMissingLogoTooltip(false)}
                disableHoverListener
                disableFocusListener
                disableTouchListener
              >
                <Box
                  sx={{ width: 'fit-content' }}
                  onClickCapture={(event) => {
                    if (logos.secondaryWide || isInheriting) return;
                    // Block mouse and keyboard clicks before the form changes.
                    event.preventDefault();
                    event.stopPropagation();
                    setShowMissingLogoTooltip(true);
                  }}
                  onMouseLeave={() => setShowMissingLogoTooltip(false)}
                  onBlurCapture={() => setShowMissingLogoTooltip(false)}
                >
                  <FormAwareCheckbox<SlideConfigFormShape>
                    label="Use default logo for darker background"
                    fieldName="useDarkLogo"
                    control={control}
                    disabled={isInheriting}
                  />
                </Box>
              </Tooltip>
              <FormAwareCheckbox<SlideConfigFormShape>
                label="Anchor logo and title"
                fieldName="useDefaultLogoPosition"
                control={control}
                disabled={isInheriting}
              />
              <FormAwareCheckbox<SlideConfigFormShape>
                label="Show logo"
                fieldName="showLogo"
                control={control}
                disabled={isInheriting}
              />
            </Stack>
            <Divider />

            <Stack spacing={2}>
              <Typography variant="title-s">Footer settings</Typography>
              <FormAwareCheckbox<SlideConfigFormShape>
                label="Include footer"
                fieldName="includeFooter"
                control={control}
                disabled={isInheriting}
              />
              <SlideConfigColorInput<SlideConfigFormShape>
                heading="Footer color"
                subheading="One color for disclaimer text, page numbers, and the divider line"
                fieldName="footerColor"
                control={control}
                colorValue={values.footerColor || '#FFFFFF'}
                disabled={isInheriting || !values.includeFooter}
                placeholder="Default"
              />
            </Stack>
          </Stack>
        </Box>

        <Stack
          spacing={2}
          flex={1}
          minWidth={0}
          sx={{ position: 'sticky', top: 16 }}
        >
          <Typography variant="title-s">Preview</Typography>
          {preview}
        </Stack>
      </Stack>
    </Stack>
  );
}
